import React, { useState, useEffect, useRef } from 'react';
import { Lock, Clock, Zap, ChevronLeft, ArrowRight, Key, PlayCircle, Trophy, CheckCircle2, Circle, FileText, Download } from 'lucide-react';

// ==================================================================================
// 🔧 维护区域 1：用户密钥库
// ==================================================================================
const VALID_KEYS = [
  'TCG-2024', 'TCG-8888',
  'K82-M9A', 'J73-X2P', 'L91-B5Q', 'R44-Z8W', 'T27-C3E', 
  'N55-H1Y', 'P66-Q9L', 'W33-A4S', 'M11-D7F', 'X99-G2H',
];

// ==================================================================================
// 🔧 维护区域 2：全新课程目录 (已按最新 Word 文档更新)
// ==================================================================================
// 💡 快速填链接技巧：
// 你的视频链接固定格式为：https://xiaoduo-unboxing-2026.oss-cn-hangzhou.aliyuncs.com/视频文件名.mp4
// 我已经把第一章填好了，后续章节你可以直接复制这个链接，只改最后的“课程x.x.mp4”即可！

const COURSE_DATA = [
  {
    id: "chap_1", title: "第一章：前期准备", subtitle: "PREPARATION", desc: "0基础入局，从注册到开店赢在起跑线", color: "from-purple-600 to-indigo-600",
    episodes: [
      { id: "ep_1_1", title: "01. 【心态】0基础以什么心态入局，最有利于后面的变现", duration: "00:00", locked: false, videoUrl: "https://xiaoduo-unboxing-2026.oss-cn-hangzhou.aliyuncs.com/%E8%AF%BE%E7%A8%8B1.1.mp4" },
      { id: "ep_1_2", title: "02. 【账号注册避坑】从注册账号就领先同行", duration: "00:00", locked: true, videoUrl: "https://xiaoduo-unboxing-2026.oss-cn-hangzhou.aliyuncs.com/%E8%AF%BE%E7%A8%8B1.2.mp4" },
      { id: "ep_1_3", title: "03. 【抖音开店全流程】从0到店铺能用", duration: "00:00", locked: true, videoUrl: "https://xiaoduo-unboxing-2026.oss-cn-hangzhou.aliyuncs.com/%E8%AF%BE%E7%A8%8B1.3.mp4" },
      { id: "ep_1_4", title: "04. 【小红书开店全流程】从0到店铺能用", duration: "00:00", locked: true, videoUrl: "https://xiaoduo-unboxing-2026.oss-cn-hangzhou.aliyuncs.com/%E8%AF%BE%E7%A8%8B1.4.mp4" },
      { id: "ep_1_5", title: "05. 【物流模版】是什么？如何设置（照抄版）", duration: "00:00", locked: true, videoUrl: "https://xiaoduo-unboxing-2026.oss-cn-hangzhou.aliyuncs.com/%E8%AF%BE%E7%A8%8B1.5.mp4" },
      { id: "ep_1_6", title: "06. 【提现&开通支付宝/微信】少一个支付，你少一半成交", duration: "00:00", locked: true, videoUrl: "https://xiaoduo-unboxing-2026.oss-cn-hangzhou.aliyuncs.com/%E8%AF%BE%E7%A8%8B1.6.mp4" },
      { id: "ep_1_7", title: "07. 营业执照要不要办？个人/个体户/公司怎么选更省心", duration: "00:00", locked: true, videoUrl: "https://xiaoduo-unboxing-2026.oss-cn-hangzhou.aliyuncs.com/%E8%AF%BE%E7%A8%8B1.7.mp4" },
      // 💡 提示：你的截图里还有个 课程0.0.mp4，这里暂时填 0.0，如果有需要你可以自己改成别的数字
      { id: "ep_1_8", title: "08. 灯光怎么选才“显卡值钱”？（便宜也能出质感）", duration: "00:00", locked: true, videoUrl: "https://xiaoduo-unboxing-2026.oss-cn-hangzhou.aliyuncs.com/%E8%AF%BE%E7%A8%8B1.8.mp4" }
    ]
  },
  {
    id: "chap_2", title: "第二章：卡牌基础", subtitle: "CARD BASICS", desc: "懂卡懂用户，才能把废纸卖出黄金价", color: "from-blue-600 to-cyan-600",
    episodes: [
      { id: "ep_2_1", title: "10. 拆卡直播到底卖的是什么？不是“卡”，是这4种东西", duration: "00:00", locked: true, videoUrl: "" },
      { id: "ep_2_2", title: "11. 【图鉴】卡牌唯一说明书（至关重要）", duration: "00:00", locked: true, videoUrl: "" },
      { id: "ep_2_3", title: "12. 卡牌类别&卡牌稀有度类别（速通）", duration: "00:00", locked: true, videoUrl: "" },
      { id: "ep_2_4", title: "13. 顾客会对卡牌“着迷”的3大原因：懂用户才能更好的卖货", duration: "00:00", locked: true, videoUrl: "" },
      { id: "ep_2_5", title: "14. 玩法介绍以及玩法的演变过程", duration: "00:00", locked: true, videoUrl: "" },
      { id: "ep_2_6", title: "15. 新手必懂的“溢价点”：让顾客心甘情愿加钱的关键", duration: "00:00", locked: true, videoUrl: "" }
    ]
  },
  {
    id: "chap_3", title: "第三章：店铺实操", subtitle: "STORE OPS", desc: "上架与活动设置，解决流量与转化", color: "from-emerald-600 to-teal-600",
    episodes: [
      { id: "ep_3_1", title: "16. 【上链接全流程】店铺类目怎么选？选错=流量白给+容易违规", duration: "00:00", locked: true, videoUrl: "" },
      { id: "ep_3_2", title: "17. 【上链接全流程】链接标题怎么写更容易下单？（直接给模版）", duration: "00:00", locked: true, videoUrl: "" },
      { id: "ep_3_3", title: "18. 【上链接全流程】SKU怎么设：不懂SKU你一定越卖越乱", duration: "00:00", locked: true, videoUrl: "" },
      { id: "ep_3_4", title: "19. 【上链接全流程】3个至关重要的设置点，设置错只能白忙活", duration: "00:00", locked: true, videoUrl: "" },
      { id: "ep_3_5", title: "20. 优惠券怎么设才有效？别再无脑9折了", duration: "00:00", locked: true, videoUrl: "" },
      { id: "ep_3_6", title: "21. 限购怎么设才能有效拉新？", duration: "00:00", locked: true, videoUrl: "" }
    ]
  },
  {
    id: "chap_4", title: "第四章：拆卡流程&直播间", subtitle: "LIVE STREAM", desc: "实战排演，打造高留存、高转化的直播间", color: "from-orange-600 to-red-600",
    episodes: [
      { id: "ep_4_1", title: "22. 拆卡直播全流程总览：从开播到收尾跑通", duration: "00:00", locked: true, videoUrl: "" },
      { id: "ep_4_2", title: "23. 直播间布置：小白也能搭出“专业感”", duration: "00:00", locked: true, videoUrl: "" },
      { id: "ep_4_3", title: "24. 镜头位怎么摆：卡面清晰=成交基础", duration: "00:00", locked: true, videoUrl: "" },
      { id: "ep_4_4", title: "25. 灯光怎么打：解决反光/发灰/看不清", duration: "00:00", locked: true, videoUrl: "" },
      { id: "ep_4_5", title: "26. 没有人说什么 & 有人说什么：决定留人还是秒划走", duration: "00:00", locked: true, videoUrl: "" },
      { id: "ep_4_6", title: "27. 【退款】如何有效避免退款？", duration: "00:00", locked: true, videoUrl: "" }
    ]
  },
  {
    id: "chap_5", title: "第五章：发货交付", subtitle: "FULFILLMENT", desc: "稳健的后勤保障，决定利润与复购", color: "from-pink-600 to-rose-600",
    episodes: [
      { id: "ep_5_1", title: "28. 拆卡发货方式怎么选：适合小白的方案", duration: "00:00", locked: true, videoUrl: "" },
      { id: "ep_5_2", title: "29. 发货全流程：从下单到交给快递员（照做版）", duration: "00:00", locked: true, videoUrl: "" },
      { id: "ep_5_3", title: "30. 怎么联系快递员：稳定上门+不吃亏", duration: "00:00", locked: true, videoUrl: "" },
      { id: "ep_5_4", title: "31. 售后怎么处理：破损/少发/错发标准流程+话术", duration: "00:00", locked: true, videoUrl: "" }
    ]
  },
  {
    id: "chap_6", title: "第六章：货源", subtitle: "SUPPLY CHAIN", desc: "找到源头利润，规避压货风险", color: "from-amber-500 to-yellow-600",
    episodes: [
      { id: "ep_6_1", title: "32. 货源从哪来：新手最稳的3条路", duration: "00:00", locked: true, videoUrl: "" },
      { id: "ep_6_2", title: "33. 判断货源靠谱不踩坑：4个标准", duration: "00:00", locked: true, videoUrl: "" },
      { id: "ep_6_3", title: "34. 小单试货怎么做：别一上来压货", duration: "00:00", locked: true, videoUrl: "" },
      { id: "ep_6_4", title: "35. 如何充分利用好你的代理，让你的钱利益最大化", duration: "00:00", locked: true, videoUrl: "" }
    ]
  },
  {
    id: "chap_7", title: "附赠加餐", subtitle: "BONUS DOCS", desc: "可直接照抄的保姆级开播资料包", color: "from-zinc-500 to-gray-700",
    episodes: [
      { id: "ep_7_1", type: "doc", title: "36. 加餐1：设备物料清单（预算版/进阶版）", docUrl: "#", cover: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?q=80&w=2670&auto=format&fit=crop" },
      { id: "ep_7_2", type: "doc", title: "37. 加餐2：话术库（开播、报卡、引导下单、售后）PDF", docUrl: "#", cover: "https://images.unsplash.com/photo-1555099962-4199c345e5dd?q=80&w=2670&auto=format&fit=crop" },
      { id: "ep_7_3", type: "doc", title: "38. 加餐3：运费/物流模板截图示例（可照抄）", docUrl: "#", cover: "https://images.unsplash.com/photo-1611162617474-5b21e879e113?q=80&w=2574&auto=format&fit=crop" },
      { id: "ep_7_4", type: "doc", title: "39. 加餐4：新手常见违规雷区清单（避免封禁/限流）", docUrl: "#", cover: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?q=80&w=2671&auto=format&fit=crop" }
    ]
  }
];

const App = () => {
  const [viewMode, setViewMode] = useState('auth'); 
  const [accessKey, setAccessKey] = useState("");
  const [currentEp, setCurrentEp] = useState(null);
  const [lastWatched, setLastWatched] = useState(null);
  const videoRef = useRef(null);

  // 进度跟踪状态
  const [completedEps, setCompletedEps] = useState([]);
  const totalEps = COURSE_DATA.reduce((acc, chap) => acc + chap.episodes.length, 0);

  useEffect(() => {
    const savedKey = localStorage.getItem('tcg_course_key');
    const history = localStorage.getItem('tcg_course_history');
    
    if (savedKey && VALID_KEYS.includes(savedKey)) {
      if (history) setLastWatched(JSON.parse(history));
      const savedProgress = localStorage.getItem(`tcg_progress_${savedKey}`);
      if (savedProgress) setCompletedEps(JSON.parse(savedProgress));
      
      setViewMode('lobby');
    }
  }, []);

  useEffect(() => {
    if (currentEp && viewMode === 'player') {
      localStorage.setItem('tcg_course_history', JSON.stringify(currentEp));
      
      // 只有视频类型才执行播放器重置逻辑
      if(currentEp.type !== 'doc' && videoRef.current) {
          videoRef.current.load();
          videoRef.current.muted = false;
          videoRef.current.volume = 1.0;
          const playPromise = videoRef.current.play();
          if (playPromise !== undefined) {
            playPromise.catch(error => console.log("需手动播放", error));
          }
      }
    }
  }, [currentEp, viewMode]);

  const toggleComplete = (epId, e) => {
    if (e) e.stopPropagation(); 
    
    setCompletedEps(prev => {
      const newProgress = prev.includes(epId) 
        ? prev.filter(id => id !== epId) 
        : [...prev, epId];
        
      const savedKey = localStorage.getItem('tcg_course_key');
      localStorage.setItem(`tcg_progress_${savedKey}`, JSON.stringify(newProgress));
      return newProgress;
    });
  };

  const handleUnlock = (e) => {
    e.preventDefault();
    const inputKey = accessKey.trim();
    if (VALID_KEYS.includes(inputKey) || inputKey === "ADMIN-MASTER-KEY") {
      localStorage.setItem('tcg_course_key', inputKey);
      
      const savedProgress = localStorage.getItem(`tcg_progress_${inputKey}`);
      if (savedProgress) setCompletedEps(JSON.parse(savedProgress));
      else setCompletedEps([]); 

      setViewMode('lobby');
    } else {
      alert("无效的密钥\n请检查是否输错，或联系管理员获取。");
    }
  };

  const handleEnterCourse = (chapterIndex = 0) => {
    const chapter = COURSE_DATA[chapterIndex];
    setCurrentEp(chapter.episodes[0]);
    setViewMode('player');
    window.scrollTo(0, 0);
  };

  if (viewMode === 'auth') {
    return (
      <div className="min-h-screen bg-[#09090b] text-white flex flex-col items-center justify-center p-6 relative overflow-hidden font-sans">
        <div className="absolute top-[-10%] right-[-10%] w-96 h-96 bg-purple-600/20 blur-[100px] rounded-full"></div>
        <div className="absolute bottom-[-10%] left-[-10%] w-96 h-96 bg-blue-600/10 blur-[100px] rounded-full"></div>
        <div className="z-10 w-full max-w-md space-y-8 text-center animate-in fade-in slide-in-from-bottom-4 duration-700">
          <div className="relative inline-block">
            <h1 className="text-5xl font-extrabold tracking-tighter italic bg-clip-text text-transparent bg-gradient-to-r from-white via-gray-200 to-gray-500">
              UNBOXING
            </h1>
            <div className="absolute -right-6 -top-4 rotate-12">
              <span className="bg-yellow-400 text-black text-xs font-bold px-2 py-1 rounded-sm shadow-[2px_2px_0px_rgba(255,255,255,0.5)] transform">
                MASTER CLASS
              </span>
            </div>
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-light text-gray-300">拆卡直播 · 小多运营实战课</h2>
            <p className="text-sm text-gray-500 font-mono">39 Episodes • 1080P HDR</p>
          </div>
          <form onSubmit={handleUnlock} className="relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl blur opacity-25 group-hover:opacity-50 transition duration-500"></div>
            <div className="relative bg-[#18181b] p-1 rounded-xl flex items-center border border-white/10">
              <Key className="ml-3 w-5 h-5 text-gray-500" />
              <input 
                type="text" 
                value={accessKey}
                onChange={(e) => setAccessKey(e.target.value)}
                placeholder="请输入您的专属密钥"
                className="w-full bg-transparent border-none focus:ring-0 text-white placeholder-gray-600 px-4 py-3 tracking-widest text-center uppercase font-mono"
              />
              <button type="submit" className="bg-white text-black px-6 py-2 rounded-lg font-bold hover:bg-gray-200 transition-colors whitespace-nowrap">
                进入课程
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (viewMode === 'lobby') {
    const progressPct = Math.round((completedEps.length / totalEps) * 100) || 0;

    return (
      <div className="min-h-screen bg-[#09090b] text-gray-100 font-sans selection:bg-purple-500/30">
        <nav className="fixed top-0 w-full z-40 bg-[#09090b]/80 backdrop-blur-md border-b border-white/5 h-16 flex items-center px-4 lg:px-8 justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-tr from-purple-500 to-blue-600 rounded-lg flex items-center justify-center transform -rotate-6 border border-white/20 shadow-lg">
              <Zap className="w-4 h-4 text-white fill-current" />
            </div>
            <span className="font-bold tracking-tight italic text-lg">UNBOXING<span className="text-purple-500">.</span>PRO</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-8 h-8 rounded-full bg-gray-800 border border-white/10 flex items-center justify-center">
              <span className="text-xs font-bold">U</span>
            </div>
          </div>
        </nav>

        <div className="pt-24 pb-10 px-4 lg:px-8 max-w-6xl mx-auto animate-in fade-in duration-500">
          
          {/* 进度面板 */}
          <div className="mb-12 bg-gradient-to-r from-[#121214] to-[#1a1a1e] border border-white/10 rounded-2xl p-6 lg:p-8 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/10 blur-[80px] rounded-full pointer-events-none"></div>
            
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 relative z-10">
              <div className="space-y-4 flex-1">
                <div className="flex items-center gap-3">
                  <div className="bg-purple-500/20 p-2 rounded-lg">
                    <Trophy className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white tracking-tight">您的学习进度</h2>
                    <p className="text-sm text-gray-400">完成全部课程与资料包，开启高转化拆卡之路</p>
                  </div>
                </div>
                
                <div className="relative pt-2">
                  <div className="flex mb-2 items-center justify-between">
                    <div>
                      <span className="text-xs font-semibold inline-block text-purple-400 font-mono">
                        {completedEps.length} / {totalEps} 课时
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-bold inline-block text-white">
                        {progressPct}%
                      </span>
                    </div>
                  </div>
                  <div className="overflow-hidden h-2 mb-4 text-xs flex rounded-full bg-gray-800 border border-white/5">
                    <div 
                      style={{ width: `${progressPct}%` }} 
                      className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-gradient-to-r from-purple-500 to-blue-500 transition-all duration-1000 ease-out"
                    ></div>
                  </div>
                </div>
              </div>

              {lastWatched && (
                <button 
                  onClick={() => {
                    setCurrentEp(lastWatched);
                    setViewMode('player');
                  }}
                  className="flex items-center justify-center gap-2 bg-white text-black hover:bg-gray-200 px-6 py-3 rounded-xl font-bold transition-all whitespace-nowrap"
                >
                  <PlayCircle className="w-5 h-5" />
                  继续学习
                </button>
              )}
            </div>
          </div>

          <div className="mb-8 flex items-end justify-between">
            <div>
              <h2 className="text-3xl font-bold text-white mb-2 italic tracking-tight">COURSE CATALOG</h2>
              <p className="text-gray-500 font-mono text-sm">SELECT A CHAPTER TO START</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {COURSE_DATA.map((chapter, idx) => (
              <div 
                key={chapter.id}
                onClick={() => handleEnterCourse(idx)}
                className="group relative bg-[#121214] border border-white/5 rounded-2xl overflow-hidden cursor-pointer hover:border-white/20 transition-all duration-300 hover:-translate-y-1"
              >
                <div className={`h-32 bg-gradient-to-br ${chapter.color} opacity-20 group-hover:opacity-30 transition-opacity relative`}>
                   <div className="absolute bottom-4 left-4">
                      <span className="text-xs font-black text-white/40 tracking-[0.2em] uppercase block mb-1">Chapter 0{idx + 1}</span>
                      <h3 className="text-xl font-bold text-white leading-none italic">{chapter.subtitle}</h3>
                   </div>
                   <div className="absolute top-4 right-4 bg-black/40 backdrop-blur-md px-2 py-1 rounded text-xs font-mono text-white/80 border border-white/10">
                     {chapter.episodes.length} EPS
                   </div>
                </div>
                <div className="p-5 flex flex-col h-[180px]">
                  <h4 className="text-lg font-bold text-white mb-2 group-hover:text-purple-400 transition-colors">
                    {chapter.title.split('：')[1] || chapter.title}
                  </h4>
                  <p className="text-sm text-gray-500 leading-relaxed line-clamp-2 mb-4">
                    {chapter.desc}
                  </p>
                  
                  {/* 章节内的进度统计 */}
                  <div className="mt-auto flex items-center justify-between pt-4 border-t border-white/5">
                     <div className="flex items-center gap-2 text-xs text-gray-400 font-mono">
                       <CheckCircle2 className="w-4 h-4 text-purple-500/50" />
                       {chapter.episodes.filter(ep => completedEps.includes(ep.id)).length} / {chapter.episodes.length}
                     </div>
                     <span className="text-xs text-purple-400 font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                       ENTER <ArrowRight className="w-3 h-3" />
                     </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // PLAYER VIEW
  return (
    <div className="min-h-screen bg-[#09090b] text-gray-100 font-sans selection:bg-purple-500/30">
      <nav className="fixed top-0 w-full z-40 bg-[#09090b]/80 backdrop-blur-md border-b border-white/5 h-16 flex items-center px-4 lg:px-8 justify-between">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => setViewMode('lobby')}
            className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors border border-white/10"
          >
            <ChevronLeft className="w-5 h-5 text-gray-300" />
          </button>
          <div className="h-6 w-px bg-white/10"></div>
          <span className="font-bold tracking-tight italic text-lg hidden md:block">
            Playing: <span className="text-purple-500 font-normal not-italic ml-1 text-sm">{currentEp?.title.split('. ')[1] || currentEp?.title}</span>
          </span>
        </div>
      </nav>

      <div className="pt-20 pb-10 px-4 lg:px-8 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="relative aspect-video bg-black rounded-2xl overflow-hidden border border-white/10 shadow-2xl group">
             
             {/* 文档类型展示页面 */}
             {currentEp?.type === 'doc' ? (
               <div className="absolute inset-0 flex flex-col relative w-full h-full group bg-[#09090b]">
                  {/* 高级封面背景 */}
                  <img src={currentEp.cover} alt="Cover" className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-20 transition-opacity duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-[#09090b]/70 to-transparent"></div>
                  
                  {/* 内容交互区 */}
                  <div className="relative z-10 flex flex-col items-center justify-center h-full p-8 text-center animate-in fade-in zoom-in-95 duration-500">
                     <div className="w-20 h-20 bg-purple-500/20 rounded-2xl flex items-center justify-center mb-6 border border-purple-500/30 backdrop-blur-sm shadow-[0_0_30px_rgba(168,85,247,0.2)]">
                        <FileText className="w-10 h-10 text-purple-400" />
                     </div>
                     <h3 className="text-2xl lg:text-3xl font-bold text-white mb-3 tracking-tight">
                       {currentEp.title.split('. ')[1] || currentEp.title}
                     </h3>
                     <p className="text-gray-400 mb-8 max-w-md text-sm leading-relaxed">
                       本节内容为专属内部图文/文档资料。<br/>请点击下方按钮获取完整内容，下载后系统将自动为您计入学习进度。
                     </p>
                     <a 
                       href={currentEp.docUrl || '#'} 
                       target="_blank" 
                       rel="noreferrer"
                       onClick={() => {
                          if(!completedEps.includes(currentEp.id)) toggleComplete(currentEp.id);
                       }}
                       className="flex items-center gap-3 bg-white text-black hover:bg-gray-200 px-8 py-3.5 rounded-xl font-bold transition-all shadow-lg hover:shadow-white/20 hover:-translate-y-1"
                     >
                        <Download className="w-5 h-5" /> 获取该资料
                     </a>
                  </div>
               </div>
             ) : currentEp?.videoUrl ? (
               // 视频播放器
               <video 
                  ref={videoRef}
                  src={currentEp.videoUrl} 
                  controls 
                  playsInline
                  className="w-full h-full object-contain"
                  controlsList="nodownload"
                  onEnded={() => {
                     if(!completedEps.includes(currentEp.id)) toggleComplete(currentEp.id);
                  }}
               >
                 Your browser does not support the video tag.
               </video>
             ) : (
               // 未上传视频的占位符
               <div className="absolute inset-0 flex items-center justify-center bg-zinc-900 text-center p-6">
                  <div className="space-y-4">
                    <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto">
                      <Lock className="w-6 h-6 text-gray-500" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold">视频即将上线</h3>
                      <p className="text-sm text-gray-500">Video coming soon.</p>
                    </div>
                  </div>
               </div>
             )}
          </div>

          <div className="relative p-6 rounded-2xl border border-white/5 bg-[#121214]">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  {currentEp?.type === 'doc' ? '资料说明' : '本节重点'}
                </h3>
              </div>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              {COURSE_DATA.find(c => c.episodes.includes(currentEp))?.desc}
            </p>
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="sticky top-24 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold italic tracking-wide">PLAYLIST</h3>
              <span className="text-xs font-mono text-gray-500">FULL ACCESS</span>
            </div>
            <div className="space-y-4 max-h-[calc(100vh-160px)] overflow-y-auto pr-2 custom-scrollbar">
              {COURSE_DATA.map((chapter, index) => (
                <div key={chapter.id} className="group">
                  <div className="mb-2 flex items-baseline px-1">
                     <h4 className="text-sm font-bold text-gray-300">
                       <span className="mr-2 opacity-50 font-mono">0{index + 1}.</span>
                       {chapter.title.split('：')[1] || chapter.title}
                     </h4>
                  </div>
                  <div className="space-y-2">
                    {chapter.episodes.map((ep) => {
                      const isActive = currentEp?.id === ep.id;
                      const isCompleted = completedEps.includes(ep.id);
                      
                      return (
                        <div 
                          key={ep.id}
                          onClick={() => {
                            setCurrentEp(ep);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          className={`
                            relative p-3 rounded-xl border cursor-pointer transition-all duration-300
                            ${isActive 
                              ? 'bg-gradient-to-r from-purple-900/40 to-blue-900/20 border-purple-500/50 shadow-[0_0_15px_rgba(168,85,247,0.15)] translate-x-1' 
                              : 'bg-[#121214] border-white/5 hover:border-white/20 hover:bg-[#18181b]'
                            }
                          `}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {/* 打钩按钮 */}
                              <button 
                                onClick={(e) => toggleComplete(ep.id, e)}
                                className="group/btn relative flex-shrink-0"
                              >
                                {isCompleted ? (
                                  <CheckCircle2 className="w-5 h-5 text-emerald-400 transition-transform hover:scale-110" />
                                ) : (
                                  <Circle className="w-5 h-5 text-gray-600 group-hover/btn:text-gray-400 transition-colors" />
                                )}
                              </button>
                              
                              <div className="flex flex-col">
                                <span className={`text-sm font-medium pr-2 leading-tight ${isActive ? 'text-white' : (isCompleted ? 'text-gray-500' : 'text-gray-300')}`}>
                                  {ep.title.split('. ')[1] || ep.title}
                                </span>
                                
                                {/* 右侧列表区分文档和视频的图标 */}
                                {ep.type === 'doc' ? (
                                   <span className="text-[10px] text-purple-400 font-mono flex items-center gap-1 mt-1">
                                     <FileText className="w-3 h-3" /> 内部资料
                                   </span>
                                ) : (
                                   <span className="text-[10px] text-gray-600 font-mono flex items-center gap-1 mt-1">
                                     <Clock className="w-3 h-3" /> {ep.duration}
                                   </span>
                                )}
                              </div>
                            </div>
                            {isActive && <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse flex-shrink-0"></div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;